import type { DateFormatMode } from "@/lib/dateFormat";
import { t } from "@/lib/strings";

export function fmtCurrency(value: number | null | undefined, currency = "EUR"): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function fmtNumber(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("de-DE").format(value);
}

export interface RelDate {
  label: string;
  overdue: boolean;
  today: boolean;
  /** Completed AFTER the due date (a later calendar day). History, not an open
   * alert — surfaces as a subtle "verspätet" marker, never red. */
  late?: boolean;
}

export function relDate(
  iso: string | null | undefined,
  today?: string,
  done = false,
  resolvedAt?: string | null,
  mode: DateFormatMode = "relative",
): RelDate | null {
  if (!iso) return null;
  const ref = today ?? new Date().toISOString().slice(0, 10);
  const td = new Date(`${ref}T00:00:00Z`).getTime();
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime();
  if (Number.isNaN(d) || Number.isNaN(td)) return null;
  const days = Math.round((d - td) / 86_400_000);
  // "absolute" mode ("Datum" preference) shows a fixed TT.MM.JJJJ; the default
  // "relative" mode keeps the day+short-month form ("21. Aug."). The overdue /
  // today / late FLAGS below are computed identically either way — only the label
  // text differs, so the colour semantics (red overdue, amber today, "verspätet")
  // survive both modes.
  const dueDate = new Date(d);
  const dueLabel =
    mode === "absolute"
      ? new Intl.DateTimeFormat("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(dueDate)
      : new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "short" }).format(dueDate);
  // A completed item (resolved ticket / checked task) is never "überfällig" or
  // "heute" — the deadline is no longer actionable, so we show the plain due date.
  // If we know WHEN it was resolved and that's a later day than the due date, flag
  // it "verspätet" (subtle history, not an open alert).
  if (done) {
    let late = false;
    if (resolvedAt) {
      const rd = new Date(`${resolvedAt.slice(0, 10)}T00:00:00Z`).getTime();
      late = !Number.isNaN(rd) && rd > d;
    }
    return { label: dueLabel, overdue: false, today: false, late };
  }
  // In absolute mode every open item shows the fixed date, keeping the flags so
  // overdue/today still colour it.
  if (mode === "absolute") {
    return { label: dueLabel, overdue: days < 0, today: days === 0 };
  }
  if (days === 0) return { label: t.today, overdue: false, today: true };
  if (days === 1) return { label: t.tomorrow, overdue: false, today: false };
  if (days === -1) return { label: t.yesterday, overdue: true, today: false };
  // "in 5 Tagen" — dative plural (days is always ≥ 2 here, so never "1 Tag").
  if (days > 1 && days <= 7)
    return { label: `${t.in} ${days} ${t.daysDative}`, overdue: false, today: false };
  // "5 Tage überfällig" — nominative plural (adverbial accusative of duration).
  if (days < -1 && days >= -7)
    return { label: `${-days} ${t.days} ${t.overdue}`, overdue: true, today: false };
  return { label: dueLabel, overdue: days < 0, today: false };
}

export function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** Compact German date for the memo running-log dividers: "27.10.2026". */
export function formatLogDate(iso: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
}

/** German long-form date for memo titles/headers: "20. Juli 2026". */
export function formatMemoDate(iso: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(iso));
}

/** ISO-8601 week number (Monday-based) for an ISO date string — used for the
 * memo header's "KW N" chip. Same algorithm the board timeline already uses
 * locally (components/board/TimelineView.tsx), duplicated here rather than
 * imported to keep this a small dependency-free date helper. */
export function isoWeekNumber(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3); // Thursday of week 1
  const DAY_MS = 86_400_000;
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
}

export function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

// Monday-first German weekday names — the series cadence stores `default_weekday`
// as 0=Mon … 6=Sun (see MeetingSeries), so the array index maps directly.
const CADENCE_WEEKDAYS = [
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
  "Sonntag",
] as const;

/**
 * A wall-clock time as people write it: "10:00", never "10:00:00".
 *
 * Postgres `TIME` serializes with seconds, so `memo.start_time` arrives as
 * "10:00:00" — nobody schedules a meeting to the second. Every surface that
 * prints a time goes through here, so the two that exist cannot drift apart
 * (the memo header printed the raw value for months while the cadence line
 * trimmed it inline).
 */
export function formatClockTime(time: string | null | undefined): string {
  return time ? time.slice(0, 5) : "";
}

/**
 * Human cadence line for a meeting series: "Wöchentlich · Montag 10:00".
 * `weekday` is 0=Mon … 6=Sun; `startTime` is "HH:MM[:SS]" (seconds trimmed).
 * When there is no weekday there is no cadence to show → returns "" so the
 * caller can drop the whole segment. A weekday without a time keeps just the
 * day ("Wöchentlich · Montag").
 */
export function formatSeriesSchedule(
  weekday: number | null | undefined,
  startTime: string | null | undefined,
): string {
  if (weekday == null || weekday < 0 || weekday > 6) return "";
  const day = CADENCE_WEEKDAYS[weekday];
  const time = formatClockTime(startTime);
  return `Wöchentlich · ${day}${time ? ` ${time}` : ""}`;
}

/**
 * Next calendar date (YYYY-MM-DD) on/after `fromISO` that falls on `weekday`
 * (0=Mon … 6=Sun) — the same convention `formatSeriesSchedule` uses. When
 * `fromISO` already lands on that weekday it is returned unchanged. Returns
 * `null` when `weekday` is null/out of range (the series has no fixed day).
 *
 * Works on local calendar dates: `fromISO` is parsed as a local midnight and
 * the result is formatted from local getters, so a caller passing today's local
 * date never slips a day across the UTC boundary.
 */
export function nextOccurrence(weekday: number | null, fromISO: string): string | null {
  if (weekday == null || weekday < 0 || weekday > 6) return null;
  const [y, m, d] = fromISO.slice(0, 10).split("-").map(Number);
  const date = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1); // local midnight
  const current = (date.getDay() + 6) % 7; // JS Sun=0 … → Mon=0 … Sun=6
  date.setDate(date.getDate() + ((weekday - current + 7) % 7));
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * The LOCAL calendar day of `d` as YYYY-MM-DD — the key to compare a meeting /
 * due date against "today". Never `toISOString().slice(0, 10)`: that is the UTC
 * day, which in Europe/Berlin reads back the PREVIOUS day between 00:00 and
 * 02:00 (it once shifted every calendar task one day to the right).
 */
export function localDayKey(d: Date = new Date()): string {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** Coarse German duration: "30 Min." / "5 Std." / "3 Tagen". */
function formatDuration(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 60) return `${min} Min.`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs} Std.`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "1 Tag" : `${days} Tagen`;
}

export interface TicketAge {
  label: string;
  tone: "neutral" | "amber" | "red";
  frozen: boolean;
}

/**
 * A ticket's lifetime: open tickets grow from created_at ("seit 3 Tagen",
 * colour-aging); resolved tickets freeze at created→resolved ("gelöst in 6
 * Std."). Pure read-time from the two timestamps — no manual field.
 */
export function ticketAge(
  createdAt: string,
  resolvedAt: string | null,
  now: number = Date.now(),
): TicketAge | null {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return null;
  if (resolvedAt) {
    const resolved = new Date(resolvedAt).getTime();
    if (Number.isNaN(resolved)) return null;
    return {
      label: `${t.ticketResolvedPrefix} ${formatDuration(resolved - created)}`,
      tone: "neutral",
      frozen: true,
    };
  }
  const hrs = (now - created) / 3_600_000;
  const tone = hrs >= 72 ? "red" : hrs >= 24 ? "amber" : "neutral";
  return {
    label: `${t.ticketAgeOpenPrefix} ${formatDuration(now - created)}`,
    tone,
    frozen: false,
  };
}

export function ticketAgeColor(tone: TicketAge["tone"]): string {
  return tone === "red" ? "var(--danger)" : tone === "amber" ? "var(--warn)" : "var(--ink-3)";
}

/** Human file size. Shared by the file viewer and the admin backup panel. */
export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** German "time ago" — "gerade eben" / "vor 5 Min." / "vor 3 Tagen", falling
 * back to a short date past a week. The single home for what was copy-pasted
 * into RowComments, NotificationsDropdown and PostfachCard. */
export function relPast(iso: string | null | undefined): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return "";
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} ${d === 1 ? "Tag" : "Tagen"}`;
  return new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "short" });
}

/** Closure-date badge in the memo timeline: "23.12.2024". */
export function formatClosureDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}
